/* eslint-disable react/jsx-props-no-spreading */
import React, { memo } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { rules, timesOptions, welcomeMessageList } from "../data";
import { TextEditor } from "./TextEditor";
import { useApiGet } from "../api/general-service";
import { MdQuestionMark } from "react-icons/md";
import Swal from "sweetalert2";

const TermsAndRules = () => {
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext(); // Access form context

  const { data: cancellationPolicyList } = useApiGet({
    endpoint: `/cancellation-policy`,
    queryKey: ["cancellation-policy"],

    config: {
      select: (data) => {
        return data?.data?.data;
      },
    },
  });

  const smokingAllowed = watch("smoking_allowed");
  const rulesPetAllowed = watch("rules_pet_allowed");
  const partyAllowed = watch("party_allowed");
  const childrenAllowed = watch("children_allowed");

  return (
    <div className="mt-4">
      <h4 className="mb-2 text-2xl font-bold">Terms & Rules</h4>

      <div className="grid grid-cols-1 mt-4">
        <div className="col-span-1">
          <label
            htmlFor="cancellation_policy_short"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Short cancellation policy
          </label>
          <Controller
            name="cancellation_policy_short"
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                id="cancellation_policy_short"
                className={`w-full rounded-md border p-3  ${
                  errors.cancellation_policy_short?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={onChange}
              >
                <option value="">Select value</option>
                {cancellationPolicyList?.length > 0 ? (
                  cancellationPolicyList
                    ?.filter((item) => item?.type === "short")
                    ?.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.group_name}: {option.before_check_in}
                      </option>
                    ))
                ) : (
                  <option value="">No cancellation policy found</option>
                )}
              </select>
            )}
          />
          {errors.cancellation_policy_short?.message && (
            <p className="mt-1 text-sm text-red-500">
              {errors.cancellation_policy_short?.message}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1">
        <div className="col-span-1">
          <label
            htmlFor="cancellation_policy_long"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Long cancellation policy
          </label>
          <Controller
            name="cancellation_policy_long"
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                id="cancellation_policy_long"
                className={`w-full rounded-md border p-3  ${
                  errors.cancellation_policy_long?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={onChange}
              >
                <option value="">Select value</option>
                {cancellationPolicyList
                  ?.filter((item) => item?.type === "long")
                  ?.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.group_name}: {option.before_check_in}
                    </option>
                  ))}
              </select>
            )}
          />
          {errors.cancellation_policy_long?.message && (
            <p className="mt-1 text-sm text-red-500">
              {errors.cancellation_policy_long?.message}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="col-span-1">
          <label
            htmlFor="min_day_booking"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Minimum days of booking
          </label>
          <Controller
            name="min_day_booking"
            control={control}
            render={({ field: { onChange, value } }) => (
              <input
                id="min_day_booking"
                type="number"
                className={`w-full rounded-md border px-4 py-3  ${
                  errors?.min_day_booking?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={onChange}
              />
            )}
          />
          {errors?.min_day_booking?.message && (
            <p className="mt-1 text-sm text-red-500">
              {errors?.min_day_booking?.message}
            </p>
          )}
        </div>
        <div className="col-span-1">
          <label
            htmlFor="max_day_booking"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Maximum days of booking
          </label>
          <Controller
            name="max_day_booking"
            control={control}
            render={({ field: { onChange, value } }) => (
              <input
                id="max_day_booking"
                type="number"
                className={`w-full rounded-md border px-4 py-3  ${
                  errors?.max_day_booking?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={onChange}
              />
            )}
          />
          {errors?.max_day_booking?.message && (
            <p className="mt-1 text-sm text-red-500">
              {errors?.max_day_booking?.message}
            </p>
          )}
        </div>

        <div className="col-span-1">
          <label
            htmlFor="check_in_after"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Check in after
          </label>
          <Controller
            name="check_in_after"
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                id="check_in_after"
                className={`w-full rounded-md border p-3  ${
                  errors.check_in_after?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={onChange}
              >
                <option value="">Select a time</option>
                {timesOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.check_in_after?.message && (
            <p className="mt-1 text-sm text-red-500">
              {errors.check_in_after?.message}
            </p>
          )}
        </div>

        <div className="col-span-1">
          <label
            htmlFor="check_out_before"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Check out before
          </label>
          <Controller
            name="check_out_before"
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                id="check_out_before"
                className={`w-full rounded-md border p-3  ${
                  errors.check_out_before?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={onChange}
              >
                <option value="">Select a time</option>
                {timesOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.check_out_before?.message && (
            <p className="mt-1 text-sm text-red-500">
              {errors.check_out_before?.message}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="col-span-1">
          <label
            htmlFor="smoking_allowed"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Smoking Allowed
          </label>
          <Controller
            name="smoking_allowed"
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                id="smoking_allowed"
                className={`w-full rounded-md border p-3  ${
                  errors?.smoking_allowed?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={onChange}
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
              </select>
            )}
          />
          {errors?.smoking_allowed?.message && (
            <p className="mt-1 text-sm text-red-500">
              {errors?.smoking_allowed?.message}
            </p>
          )}
        </div>
        <div className="col-span-1">
          {+smokingAllowed === 1 && (
            <>
              <label
                htmlFor="smoking_rules"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Smoking Rules
              </label>
              <Controller
                name="smoking_rules"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <input
                    id="smoking_rules"
                    type="text"
                    className={`w-full rounded-md border px-4 py-3  ${
                      errors?.smoking_rules?.message
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Write your condition here"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              {errors?.smoking_rules?.message && (
                <p className="mt-1 text-sm text-red-500">
                  {errors?.smoking_rules?.message}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="col-span-1">
          <label
            htmlFor="rules_pet_allowed"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Pet Allowed
          </label>
          <Controller
            name="rules_pet_allowed"
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                id="rules_pet_allowed"
                className={`w-full rounded-md border p-3  ${
                  errors?.rules_pet_allowed?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={onChange}
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
              </select>
            )}
          />
          {errors?.rules_pet_allowed?.message && (
            <p className="mt-1 text-sm text-red-500">
              {errors?.rules_pet_allowed?.message}
            </p>
          )}
        </div>
        <div className="col-span-1">
          {+rulesPetAllowed === 1 && (
            <>
              <label
                htmlFor="pet_rules"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Pet Rules
              </label>
              <Controller
                name="pet_rules"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <input
                    id="pet_rules"
                    type="text"
                    className={`w-full rounded-md border px-4 py-3  ${
                      errors?.pet_rules?.message
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Write your condition here"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              {errors?.pet_rules?.message && (
                <p className="mt-1 text-sm text-red-500">
                  {errors?.pet_rules?.message}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="col-span-1">
          <label
            htmlFor="party_allowed"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Parties Allowed
          </label>
          <Controller
            name="party_allowed"
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                id="party_allowed"
                className={`w-full rounded-md border p-3  ${
                  errors?.party_allowed?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={onChange}
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
              </select>
            )}
          />
          {errors?.party_allowed?.message && (
            <p className="mt-1 text-sm text-red-500">
              {errors?.party_allowed?.message}
            </p>
          )}
        </div>
        <div className="col-span-1">
          {+partyAllowed === 1 && (
            <>
              <label
                htmlFor="party_rules"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Party Rules
              </label>
              <Controller
                name="party_rules"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <input
                    id="party_rules"
                    type="text"
                    className={`w-full rounded-md border px-4 py-3  ${
                      errors?.party_rules?.message
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Write your condition here"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              {errors?.party_rules?.message && (
                <p className="mt-1 text-sm text-red-500">
                  {errors?.party_rules?.message}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="col-span-1">
          <label
            htmlFor="children_allowed"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Children Allowed
          </label>
          <Controller
            name="children_allowed"
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                id="children_allowed"
                className={`w-full rounded-md border p-3  ${
                  errors?.children_allowed?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={onChange}
              >
                <option value={1}>Yes</option>
                <option value={0}>No</option>
              </select>
            )}
          />
          {errors?.children_allowed?.message && (
            <p className="mt-1 text-sm text-red-500">
              {errors?.children_allowed?.message}
            </p>
          )}
        </div>
        <div className="col-span-1">
          {+childrenAllowed === 1 && (
            <>
              <label
                htmlFor="children_rules"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Children Rules
              </label>
              <Controller
                name="children_rules"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <input
                    id="children_rules"
                    type="text"
                    className={`w-full rounded-md border px-4 py-3  ${
                      errors?.children_rules?.message
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Write your condition here"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              {errors?.children_rules?.message && (
                <p className="mt-1 text-sm text-red-500">
                  {errors?.children_rules?.message}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {childrenAllowed === 1 && (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="col-span-1">
            <Controller
              name="children_ages"
              control={control}
              render={({ field: { onChange, value } }) => (
                <div id="children_ages">
                  <p className="text-gray-700">
                    Children Ages: {value?.[0]} - {value?.[1]} Months
                  </p>
                  <input
                    type="range"
                    className="w-full"
                    min={0}
                    max={24}
                    value={value?.[0]}
                    onChange={(e) =>
                      onChange([Number(e.target.value), value?.[1]])
                    }
                  />
                  <input
                    type="range"
                    className="w-full"
                    min={0}
                    max={24}
                    value={value?.[1]}
                    onChange={(e) =>
                      onChange([value?.[0], Number(e.target.value)])
                    }
                  />
                </div>
              )}
            />
          </div>

          <div className="col-span-1">
            <Controller
              name="infant_ages"
              control={control}
              render={({ field: { onChange, value } }) => (
                <div id="infant_ages">
                  <p className="text-gray-700">
                    Infant Ages: {value?.[0]} - {value?.[1]} Years
                  </p>
                  <input
                    type="range"
                    className="w-full"
                    min={0}
                    max={17}
                    value={value?.[0]}
                    onChange={(e) =>
                      onChange([Number(e.target.value), value?.[1]])
                    }
                  />
                  <input
                    type="range"
                    className="w-full"
                    min={0}
                    max={17}
                    value={value?.[1]}
                    onChange={(e) =>
                      onChange([value?.[0], Number(e.target.value)])
                    }
                  />
                </div>
              )}
            />
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1">
        <div className="col-span-1">
          <label
            htmlFor="rules_instructions"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            House Rules (List Below)
          </label>
          <Controller
            name="rules_instructions"
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                id="rules_instructions"
                className={`w-full rounded-md border p-3  ${
                  errors.rules_instructions?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={(e) => {
                  const currentValue = e.target.value;
                  onChange(currentValue);

                  const prevRulesMessage = getValues("rules");
                  if (
                    currentValue &&
                    !prevRulesMessage.includes(currentValue)
                  ) {
                    const newChange = `${prevRulesMessage}\n${currentValue}`;
                    setValue("rules", newChange, { shouldValidate: true });
                  }
                }}
              >
                <option value="">Select a rule to add</option>
                {rules.map((option) => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1">
        <div className="col-span-1">
          <p className="mb-1 flex items-center text-start text-sm font-medium text-gray-700">
            Edit House Rules
            <MdQuestionMark
              onClick={() =>
                Swal.fire({
                  text: `Choose as many rules as you would like from the list below…


These rules will appear in your listing when your listing is published. As you click each one that you like, you will see them appear in the box below. Please edit them to make them your own. You may also simply write your own rules inside the box. These are here as ideas for you.`,
                  icon: "question",
                })
              }
              style={{
                color: "#fff",
                background: "#8ec639",
                borderRadius: "50%",
                fontWeight: "bold",
                fontSize: "1.2rem",
                marginLeft: "8px",
                boxShadow: "0 0 8px 2px #8ec639, 0 0 0 3px #c6f6d5",
                border: "2px solid #8ec639",
                cursor: "pointer",
                padding: "2px",
                animation: "pulse-glow 1.2s infinite alternate",
              }}
              className="focus:outline-none focus:ring-2 focus:ring-[#8ec639]"
              title="Info."
            />
          </p>
          <div
            className={`${
              errors?.rules?.message ? "border-red-500" : "border-gray-300"
            } rounded-md border`}
            id="rules"
          >
            <Controller
              name="rules"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextEditor
                  value={value}
                  onChange={(val) => {
                    onChange(val);
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1">
        <div className="col-span-1">
          <label
            htmlFor="welcome_message_instructions"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Welcome Information (List Below)
          </label>
          <Controller
            name="welcome_message_instructions"
            control={control}
            render={({ field: { onChange, value } }) => (
              <select
                id="welcome_message_instructions"
                className={`w-full rounded-md border p-3  ${
                  errors.welcome_message_instructions?.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={value}
                onChange={(e) => {
                  const currentValue = e.target.value;
                  onChange(currentValue);

                  const prevWelcomeMessage = getValues("welcome_message");
                  if (
                    currentValue &&
                    !prevWelcomeMessage.includes(currentValue)
                  ) {
                    const newChange = `${prevWelcomeMessage}\n${currentValue}`;
                    setValue("welcome_message", newChange, {
                      shouldValidate: true,
                    });
                  }
                }}
              >
                <option value="">Select a welcome message to add</option>
                {welcomeMessageList.map((option) => (
                  <option key={option.key} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1">
        <div className="col-span-1">
          <p className="mb-1 text-start flex items-center text-sm font-medium text-gray-700">
            Edit Welcome message
            <MdQuestionMark
              onClick={() =>
                Swal.fire({
                  text: `This welcome greeting and instructions and will be SENT AS AN EMAIL when your guest pays for booking your place. As you click each one that you like, you will see them appear in the box below. Please edit them to make them your own. You may also simply write your own rules inside the box. These are here as ideas for you.`,
                  icon: "question",
                })
              }
              style={{
                color: "#fff",
                background: "#8ec639",
                borderRadius: "50%",
                fontWeight: "bold",
                fontSize: "1.2rem",
                marginLeft: "8px",
                boxShadow: "0 0 8px 2px #8ec639, 0 0 0 3px #c6f6d5",
                border: "2px solid #8ec639",
                cursor: "pointer",
                padding: "2px",
                animation: "pulse-glow 1.2s infinite alternate",
              }}
              className="focus:outline-none focus:ring-2 focus:ring-[#8ec639]"
              title="Info."
            />
          </p>
          <div
            className={`${
              errors?.welcome_message?.message
                ? "border-red-500"
                : "border-gray-300"
            } rounded-md border`}
            id="welcome_message"
          >
            <Controller
              name="welcome_message"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextEditor
                  value={value}
                  onChange={(val) => {
                    onChange(val);
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(TermsAndRules);
